import { PrintButton } from '@/components/printing';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Eye, FilePlus2, FileSignature, Mail, Plus, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Badge, Input, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import type { BillingInterval, ContractClause, ContractDetail } from '@/lib/types';
import { formatDate, formatDateTime } from '@/lib/utils';

export const BILLING_LABELS: Record<BillingInterval, string> = { MONTHLY: 'monatlich', QUARTERLY: 'vierteljährlich', YEARLY: 'jährlich', ONCE: 'einmalig' };

export const CONTRACT_VARIABLES = [
  'customer.name',
  'customer.address',
  'contract.number',
  'contract.startDate',
  'contract.endDate',
  'contract.noticeDays',
  'contract.renewalMonths',
  'contract.price',
  'contract.services',
  'sla.serviceHours',
  'tenant.name',
];

/** Euro-Eingabe ↔ Cent */
export const centsToEuro = (c: number | null | undefined) => (c == null ? '' : (c / 100).toFixed(2).replace('.', ','));
export const euroToCents = (v: string) => {
  const n = Number(v.replace(/\./g, '').replace(',', '.'));
  return v.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

/** Bearbeitung der Vertragsklauseln (§§) mit Platzhaltern. */
export function ClausesEditor({ value, onChange, disabled }: { value: ContractClause[]; onChange: (v: ContractClause[]) => void; disabled?: boolean }) {
  const update = (i: number, patch: Partial<ContractClause>) => onChange(value.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...value];
    const [item] = next.splice(i, 1);
    next.splice(i + d, 0, item!);
    onChange(next);
  };
  return (
    <div className="grid gap-3">
      {value.length === 0 && <p className="text-sm text-muted-foreground">Keine Vertragsbedingungen – das PDF enthält dann nur Parteien, Leistungen, Laufzeit und Servicezeiten.</p>}
      {value.map((c, i) => (
        <div key={i} className="grid gap-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">§ {i + 1}</span>
            <Input value={c.heading} onChange={(e) => update(i, { heading: e.target.value })} placeholder="Überschrift" aria-label={`Überschrift § ${i + 1}`} disabled={disabled} />
            {!disabled && (
              <div className="flex shrink-0">
                <Button type="button" variant="ghost" size="icon" className="size-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Nach oben">
                  <ArrowUp />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="size-8" disabled={i === value.length - 1} onClick={() => move(i, 1)} aria-label="Nach unten">
                  <ArrowDown />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="size-8 text-destructive hover:text-destructive" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={`§ ${i + 1} entfernen`}>
                  <Trash2 />
                </Button>
              </div>
            )}
          </div>
          <Textarea rows={4} value={c.text} onChange={(e) => update(i, { text: e.target.value })} aria-label={`Text § ${i + 1}`} disabled={disabled} />
        </div>
      ))}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, { heading: '', text: '' }])}>
            <Plus /> Paragraph
          </Button>
          <p className="text-xs text-muted-foreground">
            Platzhalter: {CONTRACT_VARIABLES.map((v) => <code key={v} className="mx-0.5">{`{{${v}}}`}</code>)}
          </p>
        </div>
      )}
    </div>
  );
}

/** Erzeugte PDF-Fassungen, Versand und unterschriebene Exemplare eines Vertrags. */
export function ContractDocuments({ contract, canManage, dirty }: { contract: ContractDetail; canManage: boolean; dirty: boolean }) {
  const qc = useQueryClient();
  const [send, setSend] = useState<{ docId: string; to: string; message: string } | null>(null);
  const [signedBy, setSignedBy] = useState(contract.contact?.name ?? '');
  const [signedAt, setSignedAt] = useState(new Date().toISOString().slice(0, 10));
  const fileRef = useRef<HTMLInputElement>(null);
  const done = (c: ContractDetail) => {
    qc.setQueryData(['contracts', contract.id], c);
    qc.invalidateQueries({ queryKey: ['contracts'] });
  };
  const generate = useMutation({
    mutationFn: () => api<ContractDetail>(`/contracts/${contract.id}/documents`, { method: 'POST' }),
    onSuccess: (c) => {
      done(c);
      toast.success(`Fassung ${c.documents.find((d) => d.kind === 'GENERATED')?.version} erzeugt`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const sendMail = useMutation({
    mutationFn: () => api<ContractDetail>(`/contracts/${contract.id}/documents/${send!.docId}/send`, { method: 'POST', body: { to: send!.to, ...(send!.message.trim() ? { message: send!.message } : {}) } }),
    onSuccess: (c) => {
      done(c);
      toast.success(`An ${send!.to} gesendet`);
      setSend(null);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new window.FormData();
      fd.append('signedAt', signedAt);
      fd.append('signedBy', signedBy);
      fd.append('file', file);
      return api<ContractDetail>(`/contracts/${contract.id}/documents/signed`, { method: 'POST', form: fd });
    },
    onSuccess: (c) => {
      done(c);
      toast.success('Unterschriebenes Exemplar gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" asChild>
          <a href={`/api/contracts/${contract.id}/preview`} target="_blank" rel="noreferrer">
            <Eye /> Vorschau
          </a>
        </Button>
        {canManage && (
          <Button type="button" size="sm" onClick={() => generate.mutate()} loading={generate.isPending} disabled={dirty} title={dirty ? 'Zuerst speichern' : undefined}>
            <FilePlus2 /> PDF-Fassung erzeugen
          </Button>
        )}
        <PrintButton documents={[{ key: 'contract', label: 'Vertrag' }]} target={{ contractId: contract.id }} />
        {contract.signedAt ? (
          <Badge variant="success">
            <FileSignature className="size-3" /> unterzeichnet am {formatDate(contract.signedAt)}
            {contract.signedBy ? ` von ${contract.signedBy}` : ''}
          </Badge>
        ) : (
          <Badge variant="outline">noch nicht unterzeichnet</Badge>
        )}
        {dirty && <span className="text-xs text-muted-foreground">Änderungen erst speichern, dann neue Fassung erzeugen.</span>}
      </div>

      {contract.documents.length > 0 && (
        <ul className="divide-y rounded-md border">
          {contract.documents.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2 p-2.5 text-sm">
              <Badge variant={d.kind === 'SIGNED' ? 'success' : 'secondary'}>{d.kind === 'SIGNED' ? `Unterschrieben ${d.version}` : `Fassung ${d.version}`}</Badge>
              <a href={`/api/contracts/${contract.id}/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">
                {d.file.originalName}
              </a>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(d.createdAt)}
                {d.sentTo && ` · gesendet an ${d.sentTo}`}
              </span>
              {canManage && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setSend({ docId: d.id, to: contract.contact?.email ?? '', message: '' })}>
                  <Mail /> Senden
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="grid gap-2 rounded-md border border-dashed p-3 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
          <Field label="Unterschrieben von" htmlFor="sig-by">
            <Input id="sig-by" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} />
          </Field>
          <Field label="am" htmlFor="sig-at">
            <Input id="sig-at" type="date" value={signedAt} onChange={(e) => setSignedAt(e.target.value)} />
          </Field>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = '';
              }}
            />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} loading={upload.isPending}>
              <Upload /> Unterschriebenes Exemplar
            </Button>
          </div>
        </div>
      )}

      <Dialog open={send !== null} onOpenChange={(o) => !o && setSend(null)}>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMail.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>Vertrag per E-Mail senden</DialogTitle>
              <DialogDescription>Das PDF wird als Anhang versendet.</DialogDescription>
            </DialogHeader>
            <DialogBody className="grid gap-4">
              <Field label="Empfänger" htmlFor="send-to">
                <Input id="send-to" type="email" required value={send?.to ?? ''} onChange={(e) => setSend((s) => s && { ...s, to: e.target.value })} />
              </Field>
              <Field label="Nachricht (optional)" htmlFor="send-msg" hint="Leer = Standardtext mit Bitte um Rücksendung eines unterschriebenen Exemplars">
                <Textarea id="send-msg" rows={4} value={send?.message ?? ''} onChange={(e) => setSend((s) => s && { ...s, message: e.target.value })} />
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSend(null)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={sendMail.isPending} disabled={!send?.to}>
                <Mail /> Senden
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
